/**
 * Get the start (Sunday) and end (Saturday) of the current week
 * @returns Object containing formatted start and end dates (YYYY-MM-DD)
 */
export function getCurrentWeekDates(): WeekDates {
  const today = new Date();
  const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  // Calculate start of week (Sunday) using local time
  const startOfWeek = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - currentDay,
  );

  // Calculate end of week (Saturday) using local time
  const endOfWeek = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + (6 - currentDay),
  );

  return {
    startDate: formatDate(startOfWeek),
    endDate: formatDate(endOfWeek),
  };
}

/**
 * Format a Date object as YYYY-MM-DD string using local time
 * @param date - Date object to format
 * @returns Formatted date string
 */
export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Build query string for schedule API calls with optional parameters
 * @param params - Object containing optional query parameters
 * @returns Formatted query string
 */
export function buildScheduleQueryString(
  params: ScheduleQueryParams = {},
): string {
  const { startDate, endDate, driverIds, branchId, preferredDispatchIds } =
    params;
  const { startDate: defaultStart, endDate: defaultEnd } =
    getCurrentWeekDates();

  const queryParams = new URLSearchParams();

  // Use provided dates or fall back to current week defaults
  if (startDate || defaultStart) {
    queryParams.append('startDate', startDate || defaultStart);
  }

  if (endDate || defaultEnd) {
    queryParams.append('endDate', endDate || defaultEnd);
  }

  if (driverIds && driverIds.length > 0) {
    queryParams.append('driverIds', driverIds.join(','));
  }

  if (branchId) {
    queryParams.append('branchId', branchId.toString());
  }

  if (preferredDispatchIds && preferredDispatchIds.length > 0) {
    preferredDispatchIds.forEach(id => {
      queryParams.append('preferredDispatchIds', id.toString());
    });
  }

  return queryParams.toString();
}

/**
 * Get an array of dates for the current week (Sunday to Saturday)
 * @returns Array of Date objects for each day of the week
 */
export function getWeekDays(): Date[] {
  const today = new Date();
  const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  // Calculate start of week (Sunday) using local time
  const startOfWeek = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - currentDay,
  );
  const days: Date[] = [];

  for (let i = 0; i < 7; i++) {
    const day = new Date(
      startOfWeek.getFullYear(),
      startOfWeek.getMonth(),
      startOfWeek.getDate() + i,
    );
    days.push(day);
  }

  return days;
}

/**
 * Get an array of dates for a specific week starting from a given start date
 * @param startDate - Start date string in YYYY-MM-DD format
 * @returns Array of Date objects for each day of the week (7 days)
 */
export function getWeekDaysFromStartDate(startDate: string): Date[] {
  // Parse date string manually to avoid UTC conversion issues
  const [year, month, day] = startDate.split('-').map(Number);
  const start = new Date(year, month - 1, day); // month is 0-indexed
  const days: Date[] = [];

  for (let i = 0; i < 7; i++) {
    const dayDate = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate() + i,
    );
    days.push(dayDate);
  }

  return days;
}

/**
 * Check if a schedule item occurs on or spans through a specific date
 * @param item - Schedule item to check
 * @param date - Date to check against
 * @returns Boolean indicating if the item occurs on or spans through the date
 */
export function isItemOnDate(item: ScheduleItem, date: Date): boolean {
  const itemStart = new Date(item.startDateTime);
  const checkDate = new Date(date);

  // Set times to start of day for date comparison
  itemStart.setHours(0, 0, 0, 0);
  checkDate.setHours(0, 0, 0, 0);

  // If no end date, only check if it's on the start date
  if (!item.endDateTime) {
    return itemStart.getTime() === checkDate.getTime();
  }

  const itemEnd = new Date(item.endDateTime);
  itemEnd.setHours(0, 0, 0, 0);

  // Check if the date falls within the item's date range (inclusive)
  return (
    checkDate.getTime() >= itemStart.getTime() &&
    checkDate.getTime() <= itemEnd.getTime()
  );
}

/**
 * Get the position of an item within its span (first day, middle day, or last day)
 * @param item - Schedule item
 * @param date - Date to check position for
 * @returns Object with position info
 */
export function getItemSpanPosition(
  item: ScheduleItem,
  date: Date,
): {
  isFirst: boolean;
  isLast: boolean;
  isMiddle: boolean;
  totalDays: number;
  currentDay: number;
} {
  const itemStart = new Date(item.startDateTime);
  const checkDate = new Date(date);

  // Set times to start of day
  itemStart.setHours(0, 0, 0, 0);
  checkDate.setHours(0, 0, 0, 0);

  if (!item.endDateTime) {
    return {
      isFirst: true,
      isLast: true,
      isMiddle: false,
      totalDays: 1,
      currentDay: 1,
    };
  }

  const itemEnd = new Date(item.endDateTime);
  itemEnd.setHours(0, 0, 0, 0);

  const totalDays =
    Math.ceil(
      (itemEnd.getTime() - itemStart.getTime()) / (1000 * 60 * 60 * 24),
    ) + 1;
  const currentDay =
    Math.ceil(
      (checkDate.getTime() - itemStart.getTime()) / (1000 * 60 * 60 * 24),
    ) + 1;

  const isFirst = checkDate.getTime() === itemStart.getTime();
  const isLast = checkDate.getTime() === itemEnd.getTime();
  const isMiddle = !isFirst && !isLast && totalDays > 2;

  return {
    isFirst,
    isLast,
    isMiddle,
    totalDays,
    currentDay,
  };
}

/**
 * Get schedule items for a specific date
 * @param scheduleItems - Array of schedule items
 * @param date - Date to filter by
 * @returns Array of schedule items for the specified date
 */
export function getItemsForDate(
  scheduleItems: ScheduleItem[],
  date: Date,
): ScheduleItem[] {
  return scheduleItems.filter(item => isItemOnDate(item, date));
}

/**
 * Format time from datetime string
 * @param dateTimeString - ISO datetime string
 * @returns Formatted time string (HH:MM AM/PM)
 */
export function formatTime(dateTimeString: string): string {
  const date = new Date(dateTimeString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Get status color class for schedule items
 * @param item - Schedule item
 * @returns CSS class name for status color
 */
export function getStatusColorClass(item: ScheduleItem): string {
  if (item.itemType === 'Load') {
    return 'loadItem';
  }

  switch (item.status?.toLowerCase()) {
    case 'approved':
      return 'statusApproved';
    case 'pending approval':
      return 'statusPending';
    case 'denied':
      return 'statusDenied';
    default:
      return 'statusDefault';
  }
}
